import type {
  CorvidTypes,
  SdkInstance,
} from '@wix/editor-elements-types/corvid';
import { camelToKebab } from './utils';

interface CustomElementExperiments {
  useDirectProps: boolean;
  useUnidirectionalPolyfill: boolean;
}

function noop() {
  return createSdkProxy();
}

function stringifyOnDemand(value: any): string {
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return value;
}

function createSdkProxy(slotContentSdk: SdkInstance = {}) {
  return new Proxy(slotContentSdk, {
    set: (sdk: SdkInstance, propName: string, value: any) => {
      if (propName in sdk) {
        sdk[propName] = value;
      }
      return true;
    },
    get: (sdk: SdkInstance, propName: string) => {
      if (propName === 'isMethodSupported') {
        return (methodName: string) => methodName in sdk;
      }

      if (propName in sdk) {
        return sdk[propName];
      }

      return noop;
    },
  });
}

function noopCE() {
  return createCustomElementPluginSdkProxy();
}

function createCustomElementPluginSdkProxy(
  slotContentSdk: SdkInstance = {},
  experiments: CustomElementExperiments = {
    useDirectProps: false,
    useUnidirectionalPolyfill: false,
  },
) {
  return new Proxy(slotContentSdk, {
    set: (sdk: SdkInstance, propName: string, value: any) => {
      if (typeof value !== 'function') {
        sdk.setAttribute(camelToKebab(propName), stringifyOnDemand(value));
        if (experiments.useDirectProps) {
          sdk.setDirectProp(propName, value);
        }
      } else {
        if (experiments.useDirectProps) {
          sdk.setDirectMethod(propName, value);
        }
      }

      sdk[propName] = value;
      return true;
    },
    get: (sdk: SdkInstance, propName: string) => {
      if (propName === 'isMethodSupported') {
        return () => experiments.useUnidirectionalPolyfill;
      }

      if (propName in sdk) {
        return sdk[propName];
      }

      if (experiments.useUnidirectionalPolyfill) {
        return (cb: any) => {
          if (typeof cb === 'function' && experiments.useDirectProps) {
            /* eslint-disable no-console */
            console.warn(
              `Passing callbacks to plugin methods will be deprecated soon. Instead assign method directly, like: slot.${propName}(...args);`,
            );
            sdk.setDirectMethod(propName, cb);
          }
        };
      }
      return noopCE;
    },
  });
}

export const slotsPlaceholderCompFactory: CorvidTypes.CorvidSDKFactory = ({
  platformUtils,
  getSlot,
}) => {
  const useDirectProps =
    platformUtils.essentials.experiments.get(
      'specs.thunderbolt.customElementSetDirectProps',
    ) ||
    platformUtils.essentials.experiments.get(
      'specs.thunderbolt.customElementSetDirectProps_loggedIn',
    );

  const useUnidirectionalPolyfill =
    platformUtils.essentials.experiments.get(
      'specs.thunderbolt.customElementPluginUnidirectionalPolyfill',
    ) ||
    platformUtils.essentials.experiments.get(
      'specs.thunderbolt.customElementPluginUnidirectionalPolyfill_loggedIn',
    );
  const slotContentSdk = getSlot('slot');
  const pluginType = slotContentSdk?.type;
  return {
    get slot() {
      switch (pluginType) {
        case '$w.CustomElementComponent':
          return createCustomElementPluginSdkProxy(slotContentSdk, {
            useDirectProps,
            useUnidirectionalPolyfill,
          });
        default:
          return createSdkProxy(slotContentSdk);
      }
    },
  };
};
